import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { createApprovalsApi } from "../api";
import { APPROVAL_TYPES, AUTHORITY_LEVELS, authorityLevelsForMin, formatLabel } from "../labels";
import type {
  ApprovalAuthorityLevel,
  ApprovalDecisionType,
  ApprovalDto,
  ApprovalType,
  CreateApprovalBody,
} from "../types";

export const AUTHORITY_COPY: Record<ApprovalAuthorityLevel, string> = {
  manager: "Manager — any other staff member may decide",
  dual: "Dual — two different Admins (or Super Admins) must approve",
  super_admin: "Super Admin — only a Super Admin may decide",
};

const SUBJECT_TYPES = [
  "business",
  "refund",
  "payout",
  "user",
  "creator_application",
  "creator_profile",
  "community",
  "moderation_case",
  "report",
  "booking",
  "support_ticket",
  "platform_configuration",
  "feature_flag",
  "maintenance_mode",
  "employee_access_assignment",
];

const EXPIRY: { value: string; label: string }[] = [
  { value: "24", label: "24 hours" },
  { value: "72", label: "3 days" },
  { value: "168", label: "7 days (default)" },
  { value: "720", label: "30 days" },
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface CreateApprovalPrefill {
  subject_type?: string;
  subject_id?: string;
  approval_type?: string;
  authority_level?: string;
}

export function CreateApprovalDialog({
  open,
  onClose,
  onSubmit,
  busy,
  error,
  prefill,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: CreateApprovalBody) => void | Promise<void>;
  busy: boolean;
  error: string | null;
  prefill?: CreateApprovalPrefill;
}) {
  const [subjectType, setSubjectType] = useState("business");
  const [subjectId, setSubjectId] = useState("");
  const [type, setType] = useState<ApprovalType>("business_verification");
  const [level, setLevel] = useState<ApprovalAuthorityLevel>("manager");
  const [summary, setSummary] = useState("");
  const [reason, setReason] = useState("");
  const [expiry, setExpiry] = useState("168");

  useEffect(() => {
    if (!open) return;
    setSubjectType(prefill?.subject_type ?? "business");
    setSubjectId(prefill?.subject_id ?? "");
    setType((prefill?.approval_type as ApprovalType) ?? "business_verification");
    setLevel((prefill?.authority_level as ApprovalAuthorityLevel) ?? "manager");
    setSummary("");
    setReason("");
    setExpiry("168");
  }, [open, prefill]);

  const validId = UUID.test(subjectId.trim());
  const ready = validId && summary.trim().length > 0 && reason.trim().length >= 3 && !busy;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Request an approval"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            onClick={() =>
              void onSubmit({
                subject_type: subjectType,
                subject_id: subjectId.trim(),
                approval_type: type,
                authority_level: level,
                summary: summary.trim(),
                reason: reason.trim(),
                expires_in_hours: Number(expiry),
              })
            }
          >
            {busy ? "Sending…" : "Send for approval"}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Text role="small" color="secondary" as="p">
          Someone other than you has to decide this. You'll get a task in your queue when they do.
        </Text>
        <Input
          label="What should be approved"
          hint="One line the approver sees in the queue, e.g. “Refund ₱1,200 to booking 7F3K for the rained-out session”."
          value={summary}
          maxLength={300}
          onChange={(e) => setSummary(e.target.value)}
          autoFocus
        />
        <Textarea
          label="Why"
          hint="The evidence and the reasoning. This stays on the record."
          value={reason}
          maxLength={2000}
          onChange={(e) => setReason(e.target.value)}
        />
        <Select
          label="Kind of approval"
          value={type}
          onChange={(e) => setType(e.target.value as ApprovalType)}
          options={APPROVAL_TYPES.filter((t) => t !== "future_governed_type").map((t) => ({ value: t, label: formatLabel(t) }))}
        />
        <Select
          label="Who can decide"
          value={level}
          onChange={(e) => setLevel(e.target.value as ApprovalAuthorityLevel)}
          options={AUTHORITY_LEVELS.map((l) => ({ value: l, label: AUTHORITY_COPY[l] }))}
        />
        <Select
          label="Record it's about"
          value={subjectType}
          onChange={(e) => setSubjectType(e.target.value)}
          options={SUBJECT_TYPES.map((t) => ({ value: t, label: formatLabel(t) }))}
        />
        <Input
          label="Record ID"
          hint="Copy it from the record's page in HQ."
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
          error={subjectId && !validId ? "Not a valid ID" : undefined}
        />
        <Select label="Expires if nobody decides within" value={expiry} onChange={(e) => setExpiry(e.target.value)} options={EXPIRY} />
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

const DECISION_COPY: Record<ApprovalDecisionType, { label: string; hint: string; variant: "accent" | "danger" | "secondary" }> = {
  approved: { label: "Approve", hint: "Optional note for the record.", variant: "accent" },
  rejected: { label: "Reject", hint: "Required. The requester sees this.", variant: "danger" },
  returned_for_changes: { label: "Return for changes", hint: "Required. Say what needs to change.", variant: "secondary" },
  abstained: { label: "Abstain", hint: "Optional. Abstaining does not count toward approval.", variant: "secondary" },
};

export function RecordDecisionDialog({
  open,
  decision,
  onClose,
  onSubmit,
  busy,
  error,
}: {
  open: boolean;
  decision: ApprovalDecisionType | null;
  onClose: () => void;
  onSubmit: (body: { decision: ApprovalDecisionType; decision_reason?: string }) => void | Promise<void>;
  busy: boolean;
  error: string | null;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  if (!decision) return null;
  const copy = DECISION_COPY[decision];
  const needsReason = decision === "rejected" || decision === "returned_for_changes";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={copy.label}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button
            variant={copy.variant}
            disabled={busy || (needsReason && !reason.trim())}
            onClick={() => void onSubmit({ decision, ...(reason.trim() ? { decision_reason: reason.trim() } : {}) })}
          >
            {busy ? "Recording…" : copy.label}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Textarea label={needsReason ? "Reason" : "Note"} hint={copy.hint} value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} autoFocus />
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function WithdrawDialog({
  open,
  onClose,
  onSubmit,
  busy,
  error,
  title = "Withdraw request",
  confirmLabel = "Withdraw",
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void | Promise<void>;
  busy: boolean;
  error: string | null;
  title?: string;
  confirmLabel?: string;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => void onSubmit(reason.trim())}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Textarea label="Reason" hint="Required, and kept on the audit record." value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} autoFocus />
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

/**
 * Select an already-approved Approval — no free-text UUID paste. Used by other
 * modules to gate a risky action on a decided maker-checker request.
 */
export function ApprovalPickerField({
  label,
  value,
  onChange,
  required = false,
  minAuthority,
  subjectType,
  subjectId,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (approvalId: string) => void;
  required?: boolean;
  minAuthority?: ApprovalAuthorityLevel | undefined;
  subjectType?: string;
  subjectId?: string;
  disabled?: boolean;
}) {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createApprovalsApi(client), [client]);
  const [options, setOptions] = useState<ApprovalDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorityFilter = authorityLevelsForMin(minAuthority);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.listApprovals({
          status: "approved",
          limit: 50,
          sort: "-requested_at",
          ...(authorityFilter ? { authority_level: authorityFilter } : {}),
          ...(subjectType ? { subject_type: subjectType } : {}),
          ...(subjectId ? { subject_id: subjectId } : {}),
        });
        if (!cancelled) setOptions(result.items);
      } catch (err) {
        if (!cancelled) {
          setOptions([]);
          setError(apiErrorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, authorityFilter, subjectType, subjectId]);

  const requestHref = (() => {
    const q = new URLSearchParams({ new: "1" });
    if (subjectType) q.set("subject_type", subjectType);
    if (subjectId) q.set("subject_id", subjectId);
    if (minAuthority) q.set("authority_level", minAuthority);
    return `/app/approvals?${q.toString()}`;
  })();

  return (
    <Stack gap={8}>
      <Select
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={[
          { value: "", label: required ? "Select an approved request…" : "None" },
          ...options.map((row) => ({
            value: row.id,
            label: `${row.summary ?? formatLabel(row.approval_type)} · ${formatLabel(row.authority_level)}${row.requester_name ? ` · by ${row.requester_name}` : ""}`,
          })),
        ]}
        disabled={disabled || loading}
      />
      {minAuthority ? (
        <Text role="small" color="secondary" as="p">
          Needs an approved request at {formatLabel(minAuthority)} level or higher.
        </Text>
      ) : null}
      {error ? <Alert tone="warning">{error}</Alert> : null}
      {!loading && options.length === 0 && !error ? (
        <Alert tone="info">
          No approved request for this yet.{" "}
          <Button variant="ghost" size="sm" onClick={() => navigate(requestHref)}>
            Request an approval
          </Button>
        </Alert>
      ) : null}
    </Stack>
  );
}
